import { useBrand } from './BrandProvider';
import { useBrandLogo } from './useBrandLogo';

/**
 * The stacked, centered brand mark (kicker eyebrow → wordmark → bronze rule)
 * for full-surface arrival moments: the welcome overlay and the founder
 * arrival screen. The sidebar and the intake header keep their own copies
 * (their sizing/layout — and, for the sidebar, a collapsed-rail state — differ
 * enough that forcing them through this one component would cost more than the
 * duplication saves); they share the image-vs-text DECISION via `useBrandLogo`,
 * not this component. If a brand change lands, update those two alongside this
 * (DESIGN.md "Brand wordmark").
 *
 * Wordmark rendering: prefers the firm's logo image (`logoLight`/`logoDark` on
 * the brand doc — see the naming-convention doc comment in brand.ts) for the
 * CURRENT resolved theme, falling back to the text wordmark whenever there is
 * no asset for that mode (e.g. a firm that only supplied a dark-surface logo
 * renders text on a light background — expected, not a bug) or the asset fails
 * to load. Fail-soft is load-bearing here: brand docs are operator-authored and
 * a logo URL can 404 or point at something CSP blocks, and the header must
 * degrade to the text lockup, never a broken-image icon or a blank gap.
 */
export function BrandLockup() {
  const { brand, shortName, kicker } = useBrand();
  const { src: logoSrc, onError } = useBrandLogo(brand, 'page');

  return (
    <div className="text-center">
      {kicker && (
        <div className="mb-1 font-sans text-[10px] font-semibold uppercase tracking-[0.18em] text-slate">
          {kicker}
        </div>
      )}
      {logoSrc ? (
        // Fixed height (width auto) reserves the wordmark's vertical space before
        // the image finishes loading, so the lockup doesn't grow/shrink around it.
        <img
          src={logoSrc}
          alt={shortName}
          height={32}
          className="mx-auto h-8 w-auto"
          onError={onError}
        />
      ) : (
        <div className="font-display text-[24px] font-semibold uppercase tracking-[0.16em] text-navy">
          {shortName}
        </div>
      )}
      <div className="mx-auto mt-2 h-px w-8 bg-bronze" />
    </div>
  );
}
